import { v4 as uuidv4 } from 'uuid'
import { AdvancedTrackLead } from '../../api/types'
import { ProviderRoleId, labelForRole } from '../../currentRole'
import { PlacedItem } from './PlannerCell'
import { WeeklyPlannerData, emptyWeeklyPlannerData } from './WeeklyContentPlanner'
import {
  ChecklistItem,
  PSYCHIATRIC_SUPERVISION_TRACK_KEY,
  PSYCHIATRY_ROW_KEY,
  MODULES_CATEGORY_ID,
  MONTHS,
  TOTAL_WEEKS,
  WEEKS_PER_MONTH,
  defaultMonthsFor,
  rowKeyFor,
} from './categories'

export type CarePlanStatus = 'draft' | 'submitted'

export type CarePlanState = {
  notesHtml: string
  status: CarePlanStatus
  expandedCategoryIds: string[]
  cellsByRowKey: Record<string, string[]>
  itemsByRowKey: Record<string, PlacedItem[]>
  customOptionsByRowKey: Record<string, string[]>
  weeklyPlanner: WeeklyPlannerData
  // Set when the plan was built from an intake note, until the psychiatrist first saves it.
  reviewPending?: boolean
}

export const emptyMonths = (): string[] => Array.from({ length: MONTHS }, () => '')

export const emptyState = (): CarePlanState => ({
  notesHtml: '',
  status: 'draft',
  expandedCategoryIds: [],
  cellsByRowKey: {},
  itemsByRowKey: {},
  customOptionsByRowKey: {},
  weeklyPlanner: emptyWeeklyPlannerData(),
})

export const SURVEYS_ROW_KEY = rowKeyFor('tracking', 'Surveys')
export const LABS_ROW_KEY = rowKeyFor('tracking', 'Labs')
export const ADVANCED_PANEL_OPTION = 'Advanced Panel'
export const ADVANCED_PANEL_FOLLOWUP_MONTHS = 3

// The psychiatrist's plan is the master plan: tracks are planned and run from it (ADR-094).
export const carePlanTitle = (role: ProviderRoleId) =>
  role === 'psychiatrist' ? 'Master Care Plan' : `${labelForRole(role)} Care Plan`

export const carePlanStateKey = (role: ProviderRoleId, uid: string) => `carePlan_${role}_${uid}`

export const isPsychiatristPlanHidden = (
  readOnly: boolean,
  role: string,
  status: CarePlanStatus | undefined
): boolean => readOnly && role === 'psychiatrist' && (status ?? 'draft') !== 'submitted'

const updateRowItems = (
  prev: CarePlanState,
  rowKey: string,
  update: (items: PlacedItem[]) => PlacedItem[]
): CarePlanState => ({
  ...prev,
  itemsByRowKey: { ...(prev.itemsByRowKey ?? {}), [rowKey]: update(prev.itemsByRowKey?.[rowKey] ?? []) },
})

const SUPERVISION_ITEM: PlacedItem = {
  id: PSYCHIATRIC_SUPERVISION_TRACK_KEY,
  option: 'Psychiatric supervision',
  trackKey: PSYCHIATRIC_SUPERVISION_TRACK_KEY,
  startMonth: 0,
  months: MONTHS,
}

const isSupervisionItem = (item: PlacedItem) => item.trackKey === PSYCHIATRIC_SUPERVISION_TRACK_KEY

// Puts the bar back if it is missing, moved or resized.
export const ensurePsychiatricSupervision = (prev: CarePlanState): CarePlanState => {
  const rowItems = prev.itemsByRowKey?.[PSYCHIATRY_ROW_KEY] ?? []
  const current = rowItems.find(isSupervisionItem)
  const inPlace =
    current?.id === SUPERVISION_ITEM.id &&
    current.startMonth === SUPERVISION_ITEM.startMonth &&
    current.months === SUPERVISION_ITEM.months &&
    current.startWeek === undefined
  if (inPlace) {
    return prev
  }
  const expanded = prev.expandedCategoryIds ?? []

  return {
    ...updateRowItems(prev, PSYCHIATRY_ROW_KEY, (items) => [
      SUPERVISION_ITEM,
      ...items.filter((item) => !isSupervisionItem(item)),
    ]),
    expandedCategoryIds: expanded.includes(MODULES_CATEGORY_ID) ? expanded : [...expanded, MODULES_CATEGORY_ID],
  }
}

const appendCustomOption = (prev: CarePlanState, rowKey: string, option: string): CarePlanState => {
  const existing = prev.customOptionsByRowKey?.[rowKey] ?? []
  if (existing.includes(option)) {
    return prev
  }
  return {
    ...prev,
    customOptionsByRowKey: {
      ...(prev.customOptionsByRowKey ?? {}),
      [rowKey]: [...existing, option],
    },
  }
}

export const applyPlaceItem = (
  prev: CarePlanState,
  rowKey: string,
  startMonth: number,
  option: string,
  months: number,
  trackKey?: string
): CarePlanState => {
  const newItem: PlacedItem = { id: uuidv4(), option, startMonth, months, ...(trackKey ? { trackKey } : null) }
  const additions = [newItem]

  if (
    rowKey === LABS_ROW_KEY &&
    option === ADVANCED_PANEL_OPTION &&
    startMonth + ADVANCED_PANEL_FOLLOWUP_MONTHS < MONTHS
  ) {
    additions.push({
      id: uuidv4(),
      option,
      startMonth: startMonth + ADVANCED_PANEL_FOLLOWUP_MONTHS,
      months,
    })
  }

  return updateRowItems(prev, rowKey, (items) => [...items, ...additions])
}

export const applyPrescribeSurvey = (prev: CarePlanState, startWeek: number, option: string): CarePlanState => {
  const occurrences: PlacedItem[] = []
  for (let week = startWeek; week < TOTAL_WEEKS; week += 2) {
    occurrences.push({ id: uuidv4(), option, startMonth: week, months: 1 })
  }

  return updateRowItems(prev, SURVEYS_ROW_KEY, (items) => [...items, ...occurrences])
}

export const applyRemoveItem = (prev: CarePlanState, rowKey: string, itemId: string): CarePlanState =>
  updateRowItems(prev, rowKey, (items) => items.filter((item) => item.id !== itemId))

export const applyRemoveItemsByOption = (prev: CarePlanState, rowKey: string, option: string): CarePlanState =>
  updateRowItems(prev, rowKey, (items) => items.filter((item) => item.option !== option))

export const applyRemoveTrackItems = (prev: CarePlanState, rowKey: string, trackKey: string): CarePlanState =>
  updateRowItems(prev, rowKey, (items) => items.filter((item) => item.trackKey !== trackKey))

export const applySetSelectedOptions = (prev: CarePlanState, rowKey: string, nextSelected: string[]): CarePlanState =>
  updateRowItems(prev, rowKey, (currentItems) => {
    const nextOptions = new Set(nextSelected)
    let items = currentItems.filter((item) => nextOptions.has(item.option))

    nextSelected.forEach((option) => {
      if (!items.some((item) => item.option === option)) {
        items = [
          ...items,
          {
            id: uuidv4(),
            option,
            startMonth: 0,
            months: defaultMonthsFor(rowKey),
          },
        ]
      }
    })

    return items
  })

export const applyAddCustomOption = (prev: CarePlanState, rowKey: string, option: string): CarePlanState =>
  applyPlaceItem(appendCustomOption(prev, rowKey, option), rowKey, 0, option, defaultMonthsFor(rowKey))

export const applyMoveItem = (prev: CarePlanState, rowKey: string, itemId: string, startMonth: number): CarePlanState =>
  updateRowItems(prev, rowKey, (items) =>
    items.map((item) => (item.id === itemId ? { ...item, startMonth, startWeek: undefined } : item))
  )

export const applySetItemLead = (
  prev: CarePlanState,
  rowKey: string,
  itemId: string,
  lead: AdvancedTrackLead
): CarePlanState =>
  updateRowItems(prev, rowKey, (items) =>
    items.map((item) => (item.id === itemId ? { ...item, lead: lead ?? undefined } : item))
  )

// An accepted track's bar moves to the week it starts on.
export const applyScheduleItem = (
  prev: CarePlanState,
  rowKey: string,
  itemId: string,
  startWeek: number
): CarePlanState =>
  updateRowItems(prev, rowKey, (items) =>
    items.map((item) =>
      item.id === itemId
        ? { ...item, startWeek, startMonth: Math.min(Math.floor(startWeek / WEEKS_PER_MONTH), MONTHS - 1) }
        : item
    )
  )

export const isChecklistItemOnPlan = (state: CarePlanState, { rowKey, label, trackKey }: ChecklistItem): boolean =>
  (state.itemsByRowKey?.[rowKey] ?? []).some((item) => (trackKey ? item.trackKey === trackKey : item.option === label))

export const applyChecklistToggle = (
  prev: CarePlanState,
  { rowKey, label, trackKey }: ChecklistItem,
  checked: boolean,
  trackStartMonth: number
): CarePlanState => {
  if (!checked) {
    return trackKey ? applyRemoveTrackItems(prev, rowKey, trackKey) : applyRemoveItemsByOption(prev, rowKey, label)
  }
  if (rowKey === SURVEYS_ROW_KEY) {
    return applyPrescribeSurvey(prev, 0, label)
  }

  return applyPlaceItem(prev, rowKey, trackKey ? trackStartMonth : 0, label, defaultMonthsFor(rowKey), trackKey)
}

export const applySetItemComment = (
  prev: CarePlanState,
  rowKey: string,
  itemId: string,
  comment: string
): CarePlanState =>
  updateRowItems(prev, rowKey, (items) =>
    items.map((item) => (item.id === itemId ? { ...item, comment: comment.trim() || undefined } : item))
  )
